"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Calendar, User, Tag, Share2, ArrowRight, Facebook, Twitter, Linkedin, Folder } from "lucide-react";



function SocialShareButtons() {
  return (
    <div className="flex gap-2">
      <Button variant="outline" size="icon" className="rounded-full w-8 h-8" aria-label="Facebook'ta Paylaş">
        <Facebook className="w-4 h-4" />
      </Button>
      <Button variant="outline" size="icon" className="rounded-full w-8 h-8" aria-label="Twitter'da Paylaş">
        <Twitter className="w-4 h-4" />
      </Button>
      <Button variant="outline" size="icon" className="rounded-full w-8 h-8" aria-label="LinkedIn'de Paylaş">
        <Linkedin className="w-4 h-4" />
      </Button>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse max-w-4xl mx-auto">
        <div className="h-8 bg-muted rounded w-3/4" />
        <div className="h-96 bg-muted rounded-xl" />
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-2/3" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-destructive font-sans mb-4">İçerik yüklenirken bir hata oluştu.</p>
        <Button onClick={() => refetch()}>Tekrar Dene</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-400 font-sans">Aradığınız içerik bulunamadı.</p>
        <Link to="/">
          <Button variant="link" className="mt-4">Anasayfaya Dön</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <header className="mb-8 text-center md:text-left">
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-slate-400 mb-4">
          {post.categories && post.categories.length > 0 && (
            <div className="flex items-center gap-1 text-amber-600 font-medium">
              <Folder className="w-4 h-4" />
              {post.categories.map((cat, i) => (
                <span key={cat.id} data-index={i}>
                  {i > 0 && ", "}
                  {cat.name}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{post.date ? new Date(post.date).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</span>
          </div>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-slate-300 leading-tight mb-6">
          {post.title}
        </h1>

        {}
        <div className="flex items-center justify-center md:justify-start gap-3 mb-8">
          <Avatar className="h-10 w-10 border border-slate-700">
            <AvatarImage src={post.author?.avatar?.['96']} alt={post.author?.name} />
            <AvatarFallback><User className="w-5 h-5" /></AvatarFallback>
          </Avatar>
          <div className="text-left">
            <p className="text-sm font-sans font-semibold text-slate-200">{post.author?.name || "Yazar"}</p>
            <p className="text-xs font-sans text-slate-400">Hukuk Bürosu Ekibi</p>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-10 rounded-xl overflow-hidden shadow-md border border-slate-800">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div 
        className="prose prose-lg prose-invert max-w-none mb-12 
        prose-headings:font-serif prose-headings:font-bold prose-headings:text-slate-300 prose-a:text-amber-600 prose-img:rounded-xl"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 py-6 border-t border-slate-800">
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="w-4 h-4 text-slate-400" />
            {post.tags.map((tag, i) => (
              <span 
                key={tag.id} 
                data-index={i}
                className="px-2.5 py-0.5 rounded-full bg-amber-950/30 border border-amber-900/30 text-xs font-medium text-amber-600"
              >
                #{tag.name}
              </span>
            ))}
          </div>
        )}
        
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Paylaş:
          </span>
          <SocialShareButtons />
        </div>
      </div>

      {}
      <div className="mt-10 p-6 md:p-8 bg-slate-900/40 rounded-xl border border-slate-800 flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left">
        <Avatar className="h-20 w-20 border-2 border-[#0a0f1d] shadow-sm">
          <AvatarImage src={post.author?.avatar?.['96']} alt={post.author?.name} />
          <AvatarFallback><User className="w-8 h-8" /></AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <h3 className="text-lg font-serif font-bold text-slate-300 mb-2">
            {post.author?.name} Hakkında
          </h3>
          <p className="text-slate-400 font-sans text-sm leading-relaxed">
            Av. Arb. Muhammed Çelik Hukuk ve Arabuluculuk Bürosu bünyesinde, hukuki süreçlerde müvekkillerimize en doğru ve güncel bilgiyi sunmak için çalışıyoruz. Hukukun üstünlüğü ve adalet ilkesiyle hareket ediyoruz.
          </p>
        </div>
      </div>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) return null;
  if (error || !posts || posts.length === 0) return null;

  return (
    <div className="mt-20 pt-10 border-t border-slate-800">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-300">
          İlginizi Çekebilecek Diğer Yazılar
        </h2>
        <Link to="/blog">
          <Button variant="ghost" className="hidden md:flex">
            Tüm Yazıları Gör <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {posts.map((post, i) => (
          <Card key={post.id} data-index={i} className="group hover:shadow-lg transition-all duration-300 border-slate-800 bg-slate-900/40 h-full flex flex-col">
            {post.featuredImage && (
              <div className="relative w-full pt-[60%] overflow-hidden rounded-t-xl">
                <img 
                  src={post.featuredImage} 
                  alt={post.title} 
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            )}
            <CardHeader className="pb-3 pt-5">
              <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                <Calendar className="w-3 h-3" />
                <span>{post.date ? new Date(post.date).toLocaleDateString('tr-TR') : ''}</span>
              </div>
              <CardTitle className="text-lg font-serif line-clamp-2 text-slate-300 group-hover:text-amber-600 transition-colors">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-grow">
              <div 
                className="text-sm font-sans text-slate-400 line-clamp-3"
                dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
              />
            </CardContent>
            <CardFooter className="pt-0 pb-5">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  Devamını Oku
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
      
      <div className="mt-8 text-center md:hidden">
        <Link to="/blog">
          <Button variant="outline" className="w-full">
            Tüm Yazıları Gör <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}



export default function SinglePostTemplate() {
  
  
  const currentWPQueryParams = (window as any).wvcClient?.getCurrentWPQueryParams() || {};
  const mainQuery = new WP_Query(currentWPQueryParams);

  
  
  const relatedQuery = new WP_Query({
    per_page: 3,
    orderby: 'date',
    order: 'desc',
    ignore_sticky: true,
  });

  return (
    <section id="single-post-template-section" className="bg-[#0a0f1d] py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <PostsProvider wp_query={mainQuery}>
          <SinglePostConsumer />
        </PostsProvider>

        {}
        <PostsProvider wp_query={{ per_page: 3, orderby: "date", order: "desc", ignore_sticky: true }}>
          <RelatedPostsConsumer />
        </PostsProvider>

      </div>
    </section>
  );
}